
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';

export const useManagerPermissions = (teamId: string | null) => {
  const { user, isAdmin, isManager } = useUser();

  const { data: canManageTeam = false, isLoading } = useQuery({
    queryKey: ['managerPermissions', user?.id, teamId],
    queryFn: async () => {
      if (!user || !teamId) return false;
      
      // Admins can manage all teams
      if (isAdmin) return true;
      
      // Check if user is a manager and has access to this team
      if (!isManager) return false;

      try {
        // Use the manager_has_team_access function
        const { data: hasAccessData, error: accessError } = await supabase
          .rpc('manager_has_team_access', {
            user_id: user.id,
            team_id: teamId
          });

        if (accessError) {
          // Fallback: Check manager_teams table directly
          const { data: managerTeamsData, error: managerTeamsError } = await supabase
            .from('manager_teams')
            .select('id')
            .eq('manager_id', user.id)
            .eq('team_id', teamId)
            .maybeSingle();

          if (managerTeamsError) {
            return false;
          }
          
          return !!managerTeamsData;
        }
        
        return hasAccessData === true;
      } catch (error) {
        console.error('Error checking manager permissions:', error);
        return false;
      }
    },
    enabled: !!user && !!teamId,
    staleTime: 300000, // 5 minutes
    gcTime: 600000, // 10 minutes
  });

  return {
    canManageTeam,
    isLoading: isLoading && !!user && !!teamId
  };
};
